/**
 * Access codes: the second factor a candidate is told separately from their link.
 *
 * ADR-004 makes the emailed link plus this code the ENTIRE security perimeter for field
 * staff, who never get accounts. There is no password to fall back on and no second
 * chance, so both halves have to be right.
 */
/**
 * Thirty symbols. The full 36 minus I, L, O, U, 0 and 1 — both members of each confusable
 * pair, not just one, because a candidate reading a code off a phone screen and typing it
 * into another device cannot be asked to know which of `0` and `O` we meant.
 *
 * U is removed as well: it is not a shape collision but it is the one letter that turns a
 * random string into an unfortunate word often enough to matter.
 *
 * 30^6 is about 7.3e8. That is deliberately modest — the code is not doing the heavy
 * lifting. The 256-bit token is, and the lockout ladder bounds online guessing to eight
 * attempts before the token is revoked outright.
 */
const ALPHABET = '23456789ABCDEFGHJKMNPQRSTVWXYZ';
export const ACCESS_CODE_LENGTH = 6;
export function generateAccessCode() {
    const bytes = new Uint8Array(ACCESS_CODE_LENGTH);
    crypto.getRandomValues(bytes);
    let code = '';
    for (const byte of bytes) {
        // Rejection-free modulo bias is negligible here (256 % 30 = 16, a ~6% skew on six of
        // thirty symbols) and the ladder bounds guessing regardless, but rejecting keeps the
        // distribution exactly uniform and costs nothing at this size.
        code += ALPHABET[byte % ALPHABET.length];
    }
    return code;
}
/**
 * What the person typed, made comparable.
 *
 * Case folding and separator stripping ONLY. Nothing here maps one character to another —
 * no "treat O as 0" helpfulness — because the alphabet already excludes every confusable
 * pair, and a substitution rule would silently shrink the keyspace.
 */
export function normalizeAccessCode(input) {
    return input.replace(/[\s-]/g, '').toUpperCase();
}
export function isWellFormedAccessCode(input) {
    const normalized = normalizeAccessCode(input);
    if (normalized.length !== ACCESS_CODE_LENGTH)
        return false;
    return [...normalized].every((character) => ALPHABET.includes(character));
}
//# sourceMappingURL=codes.js.map